import Breadcrumbs from 'components/breadcrumbs';
import Link from 'components/link';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { headerIds } from 'constants/test-constants';
import styles from './style.module.scss';
import { MarketStats, PropertyInventory, PriceTrends, TopNeighbourhoods, AgentContactCard, ClientPreferencesDisplay, RecommendedListings, InvestmentStats, NearbySchools, WalkabilityInfo } from './components';
import MarketFaqAccordion from 'components/pages/market-faq/market-faq-accordion';
import AreaSwitcher from 'components/pages/market-faq/components/area-switcher';
import CityListingsCarousel from './components/city-listings-carousel';
import { STATIC_MARKET_OVERVIEW_BY_SLUG } from './static-market-overview';

import type { Breadcrumb } from 'components/breadcrumbs';
import type { InsightsResponse } from '@zoocasa/go-search';
import type { SurroundingCities } from 'components/related-searches';
import type { CustomPageConfig } from './city_info_page_view_model';
import type { ListingSerializableProperties } from 'data/listing/client-app/types/listing';
import type { DisplayPreferences } from 'data/client-info-pages/types';

export interface CityInfoPageViewProps {
  /** The main heading/title of the page */
  heading: string;
  /** Navigation breadcrumbs */
  breadcrumbs?: readonly Breadcrumb[];
  /** Market insights data including statistics and trends */
  insightsData?: InsightsResponse;
  /** List of nearby cities */
  surroundingCities?: SurroundingCities[];
  /** Display name of the area */
  areaName?: string;
  /** Blurb/description about the area */
  areaBlurb?: string;
  /** Heading for the area blurb section */
  areaBlurbHeading?: string;
  /** Whether this is a custom (agent-created) page */
  isCustomPage?: boolean;
  /** Configuration for custom page */
  customPageConfig?: CustomPageConfig;
  /** Recommended listings for custom pages */
  recommendedListings?: ListingSerializableProperties[];
  /** Area slug for generating neighbourhood URLs */
  areaSlug?: string;
  /** Whether the request is from a search-engine crawler */
  isCrawler?: boolean;
}

const CityInfoPageView = (props: CityInfoPageViewProps) => {
  const { 
    heading, 
    breadcrumbs, 
    insightsData, 
    surroundingCities, 
    areaName,
    areaBlurb,
    areaBlurbHeading,
    isCustomPage,
    customPageConfig,
    recommendedListings,
    areaSlug,
    isCrawler,
  } = props;

  // areaSlug (electedAddress.slug) already matches the real-estate route's own slug convention
  // at every level (country: "ca"/"us", province: "ontario"/"new-york", city: "toronto-on") —
  // deriving from the display heading instead breaks for country pages, whose heading is the
  // full country name ("Canada") but whose real-estate slug is the 2-letter code ("ca").
  const viewListingsUrl = areaSlug
    ? `/${areaSlug}-real-estate`
    : `/${heading?.toLowerCase().replace(/[,\s]+/g, '-').replace(/-+/g, '-')}-real-estate`;

  // Check if we have any insights data at all (inventory or stats)
  const hasInsightsData = insightsData && (
    insightsData.MedianPurchasePrice > 0 ||
    insightsData.TotalHouses > 0 ||
    insightsData.TotalCondos > 0 ||
    insightsData.TotalTownhouses > 0 ||
    insightsData.TotalRentals > 0 ||
    insightsData.DaysOnMarket > 0 ||
    insightsData.AvgPricePerSqft > 0 ||
    insightsData.MedianRentPrice > 0
  );

  // Check if we have meaningful market stat values (not just inventory counts)
  const hasMarketStats = insightsData && (
    insightsData.MedianPurchasePrice > 0 ||
    insightsData.DaysOnMarket > 0 ||
    insightsData.AvgPricePerSqft > 0 ||
    insightsData.MedianRentPrice > 0 ||
    insightsData.SellListRatio > 0
  );

  // Get display preferences, defaulting to true if not specified
  const displayPrefs: DisplayPreferences = customPageConfig?.display_preferences || {};
  const shouldShow = (key: keyof DisplayPreferences) => {
    // For custom pages, check the preference (default to true if not set)
    // For regular pages, always show (no preferences)
    if (isCustomPage) {
      return displayPrefs[key] !== false;
    }
    return true;
  };

  return (
    <div>
      <Breadcrumbs breadcrumbs={breadcrumbs || []} />
      <div className={styles.component}>
        <section className={styles.content}>
          {/* Hero Section */}
          <div className={!isCustomPage && hasInsightsData ? styles.heroSectionCompact : styles.heroSection}>
          {/* Kept in the DOM for SEO (every page needs an H1) but hidden once the Market
              FAQ snapshot renders its own area pill — showing both read as a duplicated title. */}
          <h1
            className={!isCustomPage && hasInsightsData ? styles.visuallyHidden : styles['page-title']}
            data-testid={headerIds.pageHeading}
          >
            {heading}
          </h1>
            {shouldShow('show_area_blurb') && areaBlurbHeading && areaBlurb && (
              <div className={styles.areaBlurb}>
                <h2 className={styles.blurbHeading}>{areaBlurbHeading}</h2>
                <p className={styles.blurbText}>{areaBlurb}</p>
              </div>
            )}
            {!hasInsightsData && !isCustomPage && (
              <p className={styles.subtitle}>
                Explore city information, neighbourhood details, and more.
              </p>
            )}
          </div>

          {/* Agent Contact Card - Custom Pages Only */}
          {isCustomPage && shouldShow('show_agent_contact_card') && customPageConfig?.agent && (
            <AgentContactCard
              agent={customPageConfig.agent}
              customMessage={customPageConfig.custom_message}
            />
          )}

          {/* Client Preferences - Custom Pages Only */}
          {isCustomPage && shouldShow('show_client_preferences') && customPageConfig?.client_preferences && (
            <ClientPreferencesDisplay
              preferences={customPageConfig.client_preferences}
              clientName={customPageConfig.client_name}
            />
          )}

          {/* Recommended Listings - Custom Pages Only */}
          {isCustomPage && shouldShow('show_recommended_listings') && recommendedListings && recommendedListings.length > 0 && (
            <RecommendedListings
              listings={recommendedListings}
              clientName={customPageConfig?.client_name || 'You'}
              preferences={customPageConfig?.client_preferences}
            />
          )}

          {/* Walkability & Transportation Info */}
          {isCustomPage && shouldShow('show_walkability_info') && customPageConfig?.client_preferences && (
            <WalkabilityInfo
              areaName={areaName}
              clientPreferences={customPageConfig.client_preferences}
            />
          )}

          {/* Nearby Schools & Family Info */}
          {isCustomPage && shouldShow('show_nearby_schools') && (
            <NearbySchools
              insightsData={insightsData}
              areaName={areaName}
              clientPreferences={customPageConfig?.client_preferences}
            />
          )}

          {/* Investment Statistics */}
          {isCustomPage && shouldShow('show_investment_stats') && hasInsightsData && (
            <InvestmentStats
              insightsData={insightsData}
              areaName={areaName}
            />
          )}

          {/* Custom (agent-shared) pages keep the original section-by-section layout, gated by
              their own display preferences. Plain city pages use the Market FAQ snapshot below
              instead — same underlying data, presented as the design's single accordion rather
              than four separate sections. */}
          {isCustomPage && (
            <>
              {shouldShow('show_market_stats') && hasMarketStats && (
                <MarketStats
                  insightsData={insightsData}
                  areaName={areaName}
                  showRentals={shouldShow('show_rental_market')}
                />
              )}

              {shouldShow('show_property_inventory') && hasInsightsData && (
                <PropertyInventory
                  insightsData={insightsData}
                  areaName={areaName}
                  showRentals={shouldShow('show_rental_market')}
                />
              )}

              {shouldShow('show_price_trends') && hasInsightsData && (
                <PriceTrends
                  insightsData={insightsData}
                  areaName={areaName}
                />
              )}

              {shouldShow('show_top_neighbourhoods') && (
                <TopNeighbourhoods
                  insightsData={insightsData}
                  surroundingCities={surroundingCities}
                  areaName={areaName}
                  areaSlug={areaSlug}
                  clientName={customPageConfig?.client_name}
                />
              )}
            </>
          )}

          {/* Market FAQ snapshot — design's area-switcher header + accordion, replacing the
              section-by-section layout for plain (non-custom) city pages. */}
          {!isCustomPage && hasInsightsData && (
            <div className={styles.marketFaqSnapshot}>
              <Link href="/insights" className={styles.backToMarketsLink}>
                <ArrowBack className={styles.backToMarketsIcon} /> All markets
              </Link>
              <div className={styles.areaSwitcherWrap}>
                <AreaSwitcher
                  areaName={areaName || heading}
                  nearbyCities={surroundingCities?.map(city => ({ name: city.city, slug: city.slug }))}
                />
                <h2 className={styles.faqHeading}>Real Estate FAQ</h2>
              </div>
              <MarketFaqAccordion
                areaName={areaName}
                insightsData={insightsData}
                nearbyCities={surroundingCities?.map(city => ({ name: city.city, slug: city.slug }))}
                citySlug={areaSlug}
                staticSections={areaSlug ? STATIC_MARKET_OVERVIEW_BY_SLUG[areaSlug] : undefined}
              />
            </div>
          )}

          {/* CTA Section — custom (agent-shared) pages keep the static agent-contact CTA */}
          {isCustomPage && (
            <div className={styles.ctaSection}>
              <h2 className={styles.ctaHeading}>Ready to explore {areaName || 'this area'}?</h2>
              <p className={styles.ctaText}>
                {customPageConfig?.agent
                  ? `Contact ${customPageConfig.agent.first_name} to get started on your home search.`
                  : 'Browse available listings and find your perfect home.'}
              </p>
              {customPageConfig?.agent?.phone ? (
                <a
                  href={`tel:${customPageConfig.agent.phone}`}
                  className={styles.ctaButton}
                >
                  Contact {customPageConfig.agent.first_name}
                </a>
              ) : (
                <a href={viewListingsUrl} className={styles.ctaButton}>
                  View Listings
                </a>
              )}
            </div>
          )}

          {/* Plain city pages: a listings carousel (homepage pattern) with the
              "Ready to explore" CTA as its trailing card, instead of a static CTA box. */}
          {!isCustomPage && (
            <CityListingsCarousel
              listings={recommendedListings}
              areaName={areaName}
              viewListingsUrl={viewListingsUrl}
              isCrawler={!!isCrawler}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default CityInfoPageView;
